import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTable } from 'react-table';
import { Modal } from 'react-bootstrap';
import { faXmark, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import middleware from '../../Api/Middleware';
import { ButtonLightGrey } from '../../HouseStyle/Components/Buttons';
import ToastError from '../../Toasts/ToastError';
import ToastSuccess from '../../Toasts/ToastSuccess';
import TablePreset from '../../Table/TablePreset';
import CheckboxHeader from '../../Table/TableComponents/CheckboxHeader';
import CheckboxCell from '../../Table/TableComponents/CheckboxCell';
import SortableHeader from '../../Table/TableComponents/SortableHeader';
import '../../Modals/ModalStyle.css';

const RelatedProductsModal = ({ show, setShow, productId, relationType }) => {
  const [mode, setMode] = useState('list'); // 'list' or 'add'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchBar, setSearchBar] = useState('');
  const [pagination, setPagination] = useState({
    page_size: 10,
    page: 1,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  });
  const [order, setOrder] = useState({ orderBy: '', direction: '' });
  const [refetch, setRefetch] = useState(false);
  const [existingProducts, setExistingProducts] = useState([]);
  const [allRelations, setAllRelations] = useState([]); // Store all product relations


  // Fetch products and relations from the new endpoint
  const fetchProducts = async () => {
    setLoading(true);
    try {
      let relationStatus = mode === 'list' ? 'related' : 'not_related';
      const response = await middleware.get(
        `sp/productRelationsTableAll?page=${pagination.page}&page_size=${pagination.page_size}&filtering=product_id=${productId}|relation_status=${relationStatus}${relationStatus === 'not_related' ? '' : '|relation_type=' + relationType}`
      );
        let data = response.data.data || [];
        // If not_related, set relation_type to current relationType for all
        if (relationStatus === 'not_related') {
          data = data.map(item => ({ ...item, relation_type: relationType }));
        }
      setProducts(data);
      if (mode === 'list') setExistingProducts(data);
      setPagination({
        ...pagination,
        max_items: response.data.total || 0,
        begin_of_page: response.data.from || 0,
        end_of_page: response.data.to || 0,
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      ToastError('Fout bij het ophalen van producten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchProducts();
    }
  }, [show, mode, pagination.page, pagination.page_size, order.orderBy, order.direction, refetch, searchBar]);

  const handleSelection = (id, isSelected) => {
    setSelectedIds(prev => {
      if (isSelected) {
        // Only add if not already in the array
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        // Remove the ID
        return prev.filter(selectedId => selectedId !== id);
      }
    });
  };

  const handleSearchChange = useCallback(value => {
    setSearchBar(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleAction = async () => {
    if (selectedIds.length === 0) {
      ToastError(`Selecteer minimaal één product`);
      return;
    }

    // Deduplicate selectedIds to ensure no duplicates
    const uniqueIds = [...new Set(selectedIds)];

    try {
      if (mode === 'list') {
        // Remove related products
        let successCount = 0;
        for (const relatedProductId of uniqueIds) {
          await middleware.delete(`products/relation/${productId}/${relatedProductId}`);
          successCount++;
        }
        ToastSuccess(`${successCount} relatie${successCount > 1 ? 's' : ''} verwijderd`);
      } else {
        // Check if trying to add a relation for products that already have the opposite relation type
        const oppositeRelationType = relationType === 'upsell' ? 'cross-sell' : 'upsell';
        const oppositeRelations = allRelations.filter(r => r.relation_type === oppositeRelationType);
        const oppositeProductIds = oppositeRelations.map(r => r.related_product_id);
        const conflictingProducts = uniqueIds.filter(id => oppositeProductIds.includes(id));
        
        if (conflictingProducts.length > 0) {
          ToastError(`Sommige geselecteerde producten hebben al een ${oppositeRelationType} relatie. Verwijder eerst de ${oppositeRelationType} relatie om een ${relationType} relatie toe te voegen.`);
          return;
        }
        
        // Add new related products - API accepts comma-separated IDs
        const productIdsString = uniqueIds.join(',');
        const response = await middleware.post(`products/relation/${productId}/${productIdsString}`, { relation_type: relationType });
        // Count how many IDs were actually sent
        const addedCount = uniqueIds.length;
        ToastSuccess(`${addedCount} product${addedCount !== 1 ? 'en' : ''} toegevoegd`);
        setMode('list');
      }
      setSelectedIds([]);
      setRefetch(!refetch);
    } catch (error) {
      console.error('Error performing action:', error);
      ToastError('Er is een fout opgetreden bij het uitvoeren van de actie');
    }
  };

  // Columns are the same for both modes
  const columns = useMemo(() => [
    {
      Header: (
        <CheckboxHeader
          currentItem={products}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          pk={mode === 'add' ? 'other_product_id' : 'other_product_id'}
        />
      ),
      accessor: 'select',
      Cell: ({ row }) => (
        <CheckboxCell
          row={row}
          handleSelection={handleSelection}
          selectedIds={selectedIds}
          pk={'other_product_id'}
        />
      ),
    },
    {
      Header: () => <SortableHeader currentItem={order} setItem={setOrder} pos="start" field={'other_product_name'}>Productnaam</SortableHeader>,
      accessor: 'other_product_name',
    },
    {
      Header: () => <SortableHeader currentItem={order} setItem={setOrder} pos="start" field={'other_product_number'}>Productnummer</SortableHeader>,
      accessor: 'other_product_number',
    },
    {
      Header: () => <SortableHeader currentItem={order} setItem={setOrder} pos="start" field={'other_product_type'}>Type</SortableHeader>,
      accessor: 'other_product_type',
      Cell: ({ value }) => {
        const typeMap = { 1: 'Simpel', 2: 'Variabel', 3: 'Variatie' };
        return <span>{typeMap[value] || value}</span>;
      },
    },
  ], [mode, order, selectedIds, products]);

  const tableInstance = useTable({ columns, data: products });

  return (
    <Modal show={show} onHide={() => setShow(false)} centered size="xl">
      <Modal.Header className="modalTop d-flex justify-content-between">
        <div className="d-flex align-items-center gap-3 flex-nowrap titleRowModalRelations">
          {mode === 'add' && (
            <button 
              className="border-0 bg-transparent p-0" 
              onClick={() => {
                setMode('list');
                setSelectedIds([]);
                setPagination(prev => ({ ...prev, page: 1 }));
                setSearchBar('');
              }}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="fs-5" />
            </button>
          )}
          <h4 className="mb-0 fw-semibold">
            {mode === 'list' ? (relationType === 'upsell' ? 'Upsell' : 'Cross-sell') + ' producten' : 'Producten toevoegen'}
          </h4>
        </div>
        <button className="border-0 bg-transparent" onClick={() => setShow(false)}>
          <FontAwesomeIcon icon={faXmark} className="closeModal" />
        </button>
      </Modal.Header>

      <Modal.Body className="modalCenter">
        <div className="contendModal ProductRelationsTable w-100">
          <div className="mb-3">
            <p className="text-muted">
              {mode === 'list'
                ? (relationType === 'upsell' ? 'Beheer producten die als duurder alternatief worden aangeboden.' : 'Beheer producten die vaak samen met dit product worden gekocht.')
                : `Selecteer actieve producten om toe te voegen als ${relationType === 'upsell' ? 'upsell' : 'cross-sell'}.`}
            </p>
          </div>

          <TablePreset
            data={[{
              selectedIds,
              tableInstance,
              pagination,
              primary_key: 'other_product_id'
            }]}
            setPagination={setPagination}
            tableLoading={loading}
            handleSelection={handleSelection}
            rightButtons={mode === 'list' ? [{
              title: 'Producten toevoegen',
              function: () => {
                setSelectedIds([]);
                setMode('add');
                setPagination(prev => ({ ...prev, page: 1 }));
                setSearchBar('');
              }
            }] : []}
            actionOptions={[{
              title: mode === 'list' ? 'Verwijderen' : 'Toevoegen',
              function: handleAction
            }]}
            searchBar={[{
              shown: true,
              searchFunction: handleSearchChange,
              placeholder: 'Zoeken op productnaam, productnummer...'
            }]}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="modalBottom d-flex flex-row gap-2">
        <ButtonLightGrey text="Sluiten" onClickFunction={() => setShow(false)} />
      </Modal.Footer>
    </Modal>
  );
};

export default RelatedProductsModal;
